// CallEngine: room lifecycle, tracks, devices — framework-free (SPEC §9.1).
// React only renders; every LiveKit event is normalized here and pushed to
// subscribers as plain serializable snapshots (§9.3.1). LiveKit vocabulary
// does not leak past this package: the UI speaks attendee/meeting.

import {
  ConnectionQuality,
  DataPacket_Kind,
  type DataPublishOptions,
  DisconnectReason,
  isE2EESupported,
  type LocalVideoTrack,
  LogLevel,
  Participant,
  RemoteParticipant,
  Room,
  RoomEvent,
  setLogLevel,
  Track,
} from 'livekit-client';

import { BlurAssetPaths, BlurController, blurSupported, DEFAULT_BLUR_ASSETS } from './blur';
import { CHAT_TOPIC, decodeChat, encodeChat, PCHAT_TOPIC } from './chat';
import { decodePoll, encodePoll, POLL_TOPIC, PollWire } from './poll';
import { open as openSealed, seal as sealFrame } from './sealed';
import { decodeTimer, encodeTimer, TIMER_TOPIC, TimerWire } from './timer';
import {
  bytesToBase64,
  base64ToBytes as fileB64ToBytes,
  decodeFileRef,
  decryptFile,
  encodeFileRef,
  encryptFile,
  FILE_TOPIC,
  FileRef,
  randomFileId,
} from './files';
import { DeviceManager, StorageLike } from './devices';
import { Emitter } from './events';
import { BzKeyProvider } from './keys/BzKeyProvider';
import {
  decodeHand,
  decodeReaction,
  encodeHand,
  encodeReaction,
  HAND_TOPIC,
  RateGate,
  Reaction,
  REACTION_TOPIC,
} from './signals';
import {
  decodeRec,
  encodeRec,
  REC_HEARTBEAT_MS,
  REC_STALE_MS,
  REC_TOPIC,
} from './recording/indicator';
import { AttendeeSnapshot, E2EEState, EngineEvents, Quality, RoomConnectionState } from './state';
import { AttendeeStats } from './stats';
import { AudioConfig, defaultRoomOptions } from './tracks';

// SPEC §3.3: no livekit-tagged console output in production builds.
export function silenceSdkLogsInProd(isProd: boolean): void {
  if (isProd) setLogLevel(LogLevel.silent);
}

function isMissingDeviceError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return (
    err.name === 'NotFoundError' ||
    err.name === 'OverconstrainedError' ||
    err.message.includes('Requested device not found')
  );
}

function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function toQuality(q: ConnectionQuality): Quality {
  switch (q) {
    case ConnectionQuality.Excellent:
      return 'excellent';
    case ConnectionQuality.Good:
      return 'good';
    case ConnectionQuality.Poor:
      return 'poor';
    case ConnectionQuality.Lost:
      return 'lost';
    default:
      return 'unknown';
  }
}

/** E2EE material delivered with an admission grant (SPEC §5.4).
 * Model A: server secret (base64). Model B: the client supplies its own
 * passphrase/fragment secret (the server never sees it, §5.2). */
export interface E2EEGrant {
  model: 'a' | 'b';
  secret: string; // Model A: base64 server secret. Model B: user passphrase.
  sessionId: string;
}

export interface CallEngineOptions {
  storage: StorageLike;
  /** Bundler-specific E2EE worker factory, e.g.
   *  () => new Worker(new URL('livekit-client/e2ee-worker', import.meta.url), { type: 'module' }) */
  createE2EEWorker?: () => Worker;
  /** Self-hosted MediaPipe asset paths for background blur (§4.2). */
  blurAssets?: BlurAssetPaths;
}

export function engineSupportsE2EE(): boolean {
  return isE2EESupported();
}

export function engineSupportsBlur(): boolean {
  return blurSupported();
}

export class CallEngine {
  readonly events = new Emitter<EngineEvents>();
  readonly devices: DeviceManager;

  private room: Room | null = null;
  private state: RoomConnectionState = 'idle';
  private speaking = new Set<string>();
  private encrypted = new Map<string, boolean>();
  private hands = new Map<string, number>();
  private e2eeState: E2EEState = 'off';
  private e2eeModel: 'a' | 'b' = 'a';
  private decryptFailures = 0;
  private keyProvider: BzKeyProvider | null = null;
  // Serial receive chain for sealed data frames (see the DataReceived wiring).
  private rxChain: Promise<void> = Promise.resolve();
  private createE2EEWorker?: () => Worker;
  private reactionGate = new RateGate(5, 5_000);
  private selfMutingMic = false;
  private blur: BlurController;
  private blurPreferred = false;
  // Recording indicator (F-REC-1): local send state + remote receive state.
  private recSending = false;
  private recHeartbeat: ReturnType<typeof setInterval> | null = null;
  private recStaleTimer: ReturnType<typeof setTimeout> | null = null;
  private recActiveBy: string | null = null;

  constructor(opts: StorageLike | CallEngineOptions) {
    const options: CallEngineOptions = 'storage' in opts ? opts : { storage: opts };
    this.devices = new DeviceManager(options.storage);
    this.createE2EEWorker = options.createE2EEWorker;
    this.blur = new BlurController(options.blurAssets ?? DEFAULT_BLUR_ASSETS, () => {
      this.blurPreferred = false;
      this.events.emit('error', 'Background blur was turned off — this device can’t keep up.');
      this.touchLocal();
    });
  }

  get roomState(): RoomConnectionState {
    return this.state;
  }

  get currentE2EEState(): E2EEState {
    return this.e2eeState;
  }

  get localIdentity(): string | undefined {
    return this.room?.localParticipant.identity;
  }

  async connect(
    wsUrl: string,
    token: string,
    e2ee?: E2EEGrant,
    ice?: { iceServers?: RTCIceServer[]; forceRelay?: boolean },
    audio?: AudioConfig,
  ): Promise<void> {
    if (this.room) await this.disconnect();
    this.setState('connecting');

    const options = defaultRoomOptions(audio);
    // TURN fallback relay (F-PLAT): rtcConfig lives on the CONNECT options, not
    // the Room constructor. Offer the relay as an extra ICE candidate; forceRelay
    // (debug/acceptance-test only) pins iceTransportPolicy='relay' to prove the
    // relay path — production never forces it.
    const rtcConfig: RTCConfiguration = {};
    if (ice?.iceServers?.length) rtcConfig.iceServers = ice.iceServers;
    if (ice?.forceRelay) rtcConfig.iceTransportPolicy = 'relay';
    const connectOptions =
      Object.keys(rtcConfig).length > 0 ? { rtcConfig } : undefined;
    if (e2ee) {
      // §11 browser matrix: unsupported browsers get a clear block, never a
      // silent downgrade to an unencrypted call.
      if (!isE2EESupported() || !this.createE2EEWorker) {
        this.setState('disconnected', 'e2ee-unsupported');
        this.setE2EEState('unsupported');
        throw new Error(
          'This browser cannot run end-to-end encryption. Use a current Chrome, Edge, Firefox, or Safari 17+.',
        );
      }
      this.keyProvider = new BzKeyProvider();
      this.e2eeModel = e2ee.model;
      options.e2ee = { keyProvider: this.keyProvider, worker: this.createE2EEWorker() };
    }

    const room = new Room(options);
    this.room = room;
    this.wireRoomEvents(room);

    try {
      if (e2ee && this.keyProvider) {
        // Order matters: derive the media key AFTER the Room's E2EE manager is
        // subscribed to the provider (so its SetKey reaches the worker), then
        // switch encryption on, then connect. Model B interprets the secret as
        // a passphrase; Model A as the base64 server secret (§5.2).
        await this.keyProvider.setSessionSecret(
          e2ee.secret,
          e2ee.sessionId,
          e2ee.model === 'b' ? 'passphrase' : 'base64',
        );
        await room.setE2EEEnabled(true);
      }
      await room.connect(wsUrl, token, connectOptions);
    } catch (err) {
      this.room = null;
      this.keyProvider = null;
      this.setState('disconnected', String(err));
      throw err;
    }
    this.setE2EEState(e2ee ? 'on' : 'off');
    this.setState('connected');
    this.emitAllAttendees();
    // Apply persisted device preferences that are still available (F-CALL-3).
    for (const kind of ['audioinput', 'videoinput', 'audiooutput'] as MediaDeviceKind[]) {
      const preferred = await this.devices.preferredAvailable(kind);
      if (preferred) await room.switchActiveDevice(kind, preferred).catch(() => {});
    }
  }

  async disconnect(): Promise<void> {
    const room = this.room;
    this.room = null;
    this.speaking.clear();
    this.encrypted.clear();
    this.hands.clear();
    this.decryptFailures = 0;
    this.e2eeModel = 'a';
    // Tear down any recording-indicator timers so they can't fire post-leave.
    if (this.recHeartbeat) clearInterval(this.recHeartbeat);
    if (this.recStaleTimer) clearTimeout(this.recStaleTimer);
    this.recHeartbeat = null;
    this.recStaleTimer = null;
    this.recSending = false;
    this.recActiveBy = null;
    // §5.3 zeroization: drop the provider (its non-extractable key becomes
    // unreachable) — the SDK terminates the E2EE worker with the room.
    this.keyProvider = null;
    if (room) {
      room.removeAllListeners();
      await room.disconnect();
    }
    this.setE2EEState('off');
    this.setState('disconnected');
  }

  // ── Local media ────────────────────────────────────────────────────────────

  async setMicEnabled(enabled: boolean): Promise<void> {
    // Flag self-initiated mutes so the host-mute toast (F-HOST-2) only fires
    // for server-side mutes.
    if (!enabled) this.selfMutingMic = true;
    try {
      await this.setCaptureEnabled('audioinput', enabled);
    } finally {
      this.selfMutingMic = false;
    }
  }

  async setCameraEnabled(enabled: boolean): Promise<void> {
    await this.setCaptureEnabled('videoinput', enabled);
  }

  /**
   * Enable/disable a capture source with stale-device recovery (F-CALL-3):
   * if the remembered device is gone (unplugged webcam, vanished Continuity
   * Camera → "Requested device not found"), fall back to any available device
   * and retry once instead of failing the toggle.
   */
  private async setCaptureEnabled(kind: 'audioinput' | 'videoinput', enabled: boolean): Promise<void> {
    if (!this.room) return;
    const what = kind === 'videoinput' ? 'camera' : 'microphone';
    const apply = (on: boolean) =>
      kind === 'videoinput'
        ? this.room!.localParticipant.setCameraEnabled(on)
        : this.room!.localParticipant.setMicrophoneEnabled(on);
    try {
      await apply(enabled);
    } catch (err) {
      let failure: unknown = err;
      if (enabled && isMissingDeviceError(err)) {
        const fallback = await this.fallbackToAvailableDevice(kind);
        if (fallback) {
          try {
            await apply(true);
            this.touchLocal();
            return;
          } catch (retryErr) {
            failure = retryErr;
          }
        } else {
          this.events.emit('error', `No ${what} detected on this device`);
          return;
        }
      }
      this.events.emit('error', `Could not start the ${what}: ${errMessage(failure)}`);
      return;
    }
    this.touchLocal();
  }

  private async fallbackToAvailableDevice(kind: MediaDeviceKind): Promise<boolean> {
    if (!this.room) return false;
    const available = await this.devices.list(kind).catch(() => [] as MediaDeviceInfo[]);
    const first = available.find((d) => d.deviceId !== '');
    if (!first) return false;
    await this.room.switchActiveDevice(kind, first.deviceId).catch(() => {});
    this.devices.savePref(kind, first.deviceId);
    this.events.emit('deviceFallback', kind, first.label || 'default device');
    return true;
  }

  /** Screen/window/tab share (F-CALL-2); browser picker decides the surface. */
  async setScreenShareEnabled(enabled: boolean): Promise<void> {
    // Publish the screen as a SINGLE (non-simulcast) encoding. A two-layer
    // screen share had a bandwidth dead zone — the low layer capped at 200kbps
    // and the HD layer needed ~4Mbps to activate, so on any link between those
    // the encoder could not climb and collapsed to a 640px stream that never
    // reached HD. One layer spends whatever bandwidth exists on a single stream
    // and, with maintain-resolution, keeps resolution while shedding frame-rate
    // under load. Tradeoff: subscribers can't drop to a lower layer — acceptable
    // for a shared screen, where legibility matters more than universal reach.
    await this.room?.localParticipant.setScreenShareEnabled(
      enabled,
      { audio: false },
      enabled
        ? {
            simulcast: false,
            // NB: a screen-share track reads screenShareEncoding, not videoEncoding.
            screenShareEncoding: { maxBitrate: 4_000_000, maxFramerate: 15 },
            degradationPreference: 'maintain-resolution',
          }
        : undefined,
    );
    if (enabled) {
      // Sharpness over frame-rate: the 'detail' content hint makes WebRTC keep
      // resolution under packet loss / CPU pressure instead of blurring — shared
      // screens are mostly static text/detail, so degrading fps beats degrading
      // legibility. Set on the track after publish (getDisplayMedia leaves it 'motion').
      const track = this.room?.localParticipant.getTrackPublication(Track.Source.ScreenShare)
        ?.videoTrack?.mediaStreamTrack;
      if (track) track.contentHint = 'detail';
    }
    this.touchLocal();
  }

  // ── Data messaging (chat F-COM-1, reactions F-COM-3, hand F-COM-4) ────────
  // LiveKit's E2EE worker covers MEDIA frames only — data packets cross the
  // SFU under transport crypto, which is not end-to-end. So in encrypted
  // rooms every payload below is sealed app-side (sealed.ts) before
  // publishData and opened after DataReceived; the SFU relays opaque bytes.
  // Unencrypted rooms keep the legacy plaintext frames.

  /** Single send chokepoint for the data plane: seal when the room has a
   * session key, pass through when it doesn't. */
  private async publishSealed(payload: Uint8Array<ArrayBuffer>, opts: DataPublishOptions): Promise<void> {
    const lp = this.room?.localParticipant;
    if (!lp) return;
    const key = this.keyProvider?.dataKey;
    const wire = key ? await sealFrame(key, opts.topic ?? '', payload) : payload;
    await lp.publishData(wire, opts);
  }

  async sendChat(text: string): Promise<void> {
    const lp = this.room?.localParticipant;
    const trimmed = text.trim();
    if (!lp || !trimmed) return;
    const { payload, wire } = encodeChat(trimmed);
    await this.publishSealed(payload, { reliable: true, topic: CHAT_TOPIC });
    // Local echo — the sender's own message never comes back from the SFU.
    this.events.emit('chatMessage', {
      id: wire.id,
      fromIdentity: lp.identity,
      fromName: lp.name || lp.identity,
      text: wire.text,
      ts: wire.ts,
    });
  }

  /** Private 1:1 message to one destination identity (F-COM-2). */
  async sendPrivateChat(toIdentity: string, text: string): Promise<void> {
    const lp = this.room?.localParticipant;
    const trimmed = text.trim();
    if (!lp || !trimmed) return;
    const { payload, wire } = encodeChat(trimmed);
    await this.publishSealed(payload, {
      reliable: true,
      topic: PCHAT_TOPIC,
      destinationIdentities: [toIdentity],
    });
    this.events.emit('privateChat', toIdentity, {
      id: wire.id,
      fromIdentity: lp.identity,
      fromName: lp.name || lp.identity,
      text: wire.text,
      ts: wire.ts,
    });
  }

  /**
   * Poll message (Meeting Tools). `to` addresses a single identity (a vote goes
   * only to the host; a late-join resync goes only to the joiner); omit to
   * broadcast. No local echo — the poll store updates itself on send.
   */
  sendPoll(wire: PollWire, to?: string): void {
    void this.publishSealed(encodePoll(wire), {
      reliable: true,
      topic: POLL_TOPIC,
      ...(to ? { destinationIdentities: [to] } : {}),
    });
  }

  /** Shared-timer message (Meeting Tools). `to` addresses one identity (a
   * late-join resync); omit to broadcast. No local echo. */
  sendTimer(wire: TimerWire, to?: string): void {
    void this.publishSealed(encodeTimer(wire), {
      reliable: true,
      topic: TIMER_TOPIC,
      ...(to ? { destinationIdentities: [to] } : {}),
    });
  }

  /** Rate-limited client-side (F-COM-3); returns false when throttled. */
  sendReaction(emoji: Reaction): boolean {
    const lp = this.room?.localParticipant;
    if (!lp) return false;
    if (!this.reactionGate.tryPass()) return false;
    void this.publishSealed(encodeReaction(emoji), { reliable: false, topic: REACTION_TOPIC });
    this.events.emit('reaction', lp.identity, lp.name || lp.identity, emoji);
    return true;
  }

  async setHandRaised(raised: boolean): Promise<void> {
    const lp = this.room?.localParticipant;
    if (!lp) return;
    await this.publishSealed(encodeHand(raised), { reliable: true, topic: HAND_TOPIC });
    this.applyHand(lp.identity, raised ? Date.now() : null);
  }

  // ── Encrypted file sharing (F-COM-5) ─────────────────────────────────────
  // Crypto + data channel live here; the UI does the presign + S3 transfer
  // (it holds the room token). Only ciphertext ever leaves the client.

  /** Encrypt a file under a fresh per-file key derived from the room secret. */
  async encryptFileForSharing(
    data: ArrayBuffer,
  ): Promise<{ fileId: string; ciphertext: ArrayBuffer; nonceB64: string }> {
    if (!this.keyProvider) throw new Error('file sharing requires an encrypted meeting');
    const fileId = randomFileId();
    const key = await this.keyProvider.deriveFileKey(fileId);
    const { ciphertext, nonce } = await encryptFile(key, data);
    return { fileId, ciphertext, nonceB64: bytesToBase64(nonce) };
  }

  /** Broadcast an uploaded file's reference over the E2EE data channel. */
  async broadcastFileRef(ref: Omit<FileRef, 'v'>): Promise<void> {
    await this.publishSealed(encodeFileRef({ v: 1, ...ref }), {
      reliable: true,
      topic: FILE_TOPIC,
    });
  }

  /** Decrypt received ciphertext with the per-file key (F-COM-5). */
  async decryptSharedFile(
    fileId: string,
    nonceB64: string,
    ciphertext: ArrayBuffer,
  ): Promise<ArrayBuffer> {
    if (!this.keyProvider) throw new Error('no key for this file');
    const key = await this.keyProvider.deriveFileKey(fileId);
    return decryptFile(key, fileB64ToBytes(nonceB64), ciphertext);
  }

  // ── Recording indicator protocol (F-REC-1, §10.5) ────────────────────────

  /** Start broadcasting the recording indicator: rec:on now, on a heartbeat,
   * and re-asserted to every new join. Call when local recording begins. */
  startRecordingIndicator(byName: string): void {
    if (!this.room || this.recSending) return;
    this.recSending = true;
    this.recActiveBy = byName;
    const send = () => void this.publishRec(true, byName);
    send();
    this.recHeartbeat = setInterval(send, REC_HEARTBEAT_MS);
    this.events.emit('recordingChanged', true, byName);
  }

  /** Stop the indicator: broadcast rec:off. Call when recording stops. */
  stopRecordingIndicator(): void {
    if (!this.recSending) return;
    this.recSending = false;
    if (this.recHeartbeat) clearInterval(this.recHeartbeat);
    this.recHeartbeat = null;
    void this.publishRec(false, this.recActiveBy ?? '');
    this.recActiveBy = null;
    this.events.emit('recordingChanged', false, null);
  }

  get isRecordingActive(): boolean {
    return this.recActiveBy !== null;
  }

  private async publishRec(on: boolean, by: string): Promise<void> {
    await this.publishSealed(encodeRec(on, by), { reliable: true, topic: REC_TOPIC }).catch(
      () => {},
    );
  }

  private onRecSignal(on: boolean, by: string): void {
    if (this.recStaleTimer) clearTimeout(this.recStaleTimer);
    this.recStaleTimer = null;
    if (on) {
      const changed = this.recActiveBy === null;
      this.recActiveBy = by;
      if (changed) this.events.emit('recordingChanged', true, by);
      // Heartbeat-loss safety: clear if the recorder goes silent (§10.5).
      this.recStaleTimer = setTimeout(() => {
        this.recActiveBy = null;
        this.events.emit('recordingChanged', false, null);
      }, REC_STALE_MS);
    } else if (this.recActiveBy !== null) {
      this.recActiveBy = null;
      this.events.emit('recordingChanged', false, null);
    }
  }

  /** Host lowers another attendee's hand (F-COM-4). */
  async lowerHand(identity: string): Promise<void> {
    await this.publishSealed(encodeHand(false, identity), { reliable: true, topic: HAND_TOPIC });
    this.applyHand(identity, null);
  }

  isHandRaised(identity: string): boolean {
    return this.hands.has(identity);
  }

  /**
   * Low-bandwidth mode (F-CALL-7): unsubscribe from all remote video so only
   * audio flows. Sticky is the UI's concern (persisted flag); the engine just
   * applies it and re-applies to late joiners while active.
   */
  private lowBandwidth = false;

  /**
   * Background blur (F-CALL-5): apply/remove the pre-publish processor on the
   * local camera. Stored as a preference and re-applied when the camera
   * (re)starts; auto-disables on slow hardware.
   */
  async setBackgroundBlur(on: boolean): Promise<void> {
    this.blurPreferred = on;
    const track = this.localCameraTrack();
    if (!track) return; // applied when the camera next starts (touchLocal path)
    if (on) await this.blur.enable(track);
    else await this.blur.disable(track);
    this.touchLocal();
  }

  get isBlurActive(): boolean {
    return this.blur.active;
  }

  private localCameraTrack(): LocalVideoTrack | undefined {
    const pub = this.room?.localParticipant.getTrackPublication(Track.Source.Camera);
    const track = pub?.track;
    return track && track.kind === Track.Kind.Video ? (track as LocalVideoTrack) : undefined;
  }

  setLowBandwidth(on: boolean): void {
    this.lowBandwidth = on;
    if (!this.room) return;
    for (const p of this.room.remoteParticipants.values()) this.applyLowBandwidth(p);
  }

  get isLowBandwidth(): boolean {
    return this.lowBandwidth;
  }

  private applyLowBandwidth(p: RemoteParticipant): void {
    for (const pub of p.trackPublications.values()) {
      if (pub.kind === Track.Kind.Video) pub.setSubscribed(!this.lowBandwidth);
    }
  }

  /** Hot-swap a device mid-call without republishing (F-CALL-3). */
  async switchDevice(kind: MediaDeviceKind, deviceId: string): Promise<void> {
    if (!this.room) return;
    await this.room.switchActiveDevice(kind, deviceId);
    this.devices.savePref(kind, deviceId);
  }

  activeDeviceId(kind: MediaDeviceKind): string | undefined {
    return this.room?.getActiveDevice(kind);
  }

  // ── Read model (pull APIs used by the UI outside React state) ─────────────

  snapshots(): AttendeeSnapshot[] {
    if (!this.room) return [];
    const list: AttendeeSnapshot[] = [this.snapshotOf(this.room.localParticipant)];
    for (const p of this.room.remoteParticipants.values()) list.push(this.snapshotOf(p));
    return list;
  }

  /**
   * Track lookup for tile attach effects: React state stores flags only; the
   * effect pulls the live track here and calls attach/detach (§9.3.4).
   */
  videoTrack(identity: string, source: 'camera' | 'screen') {
    const p = this.participant(identity);
    const src = source === 'camera' ? Track.Source.Camera : Track.Source.ScreenShare;
    const pub = p?.getTrackPublication(src);
    if (pub && 'isSubscribed' in pub && !pub.isSubscribed) return undefined;
    return pub?.videoTrack;
  }

  audioTrack(identity: string) {
    const p = this.participant(identity);
    if (!p || p.isLocal) return undefined; // never play back the local mic
    const pub = p.getTrackPublication(Track.Source.Microphone);
    return pub?.audioTrack;
  }

  // ── Recording capture surface (F-REC-1) ──────────────────────────────────
  // The recorder composites what the local participant can actually see/hear —
  // i.e. already-DECRYPTED media. It reads raw MediaStreamTracks; the E2EE
  // worker has decrypted inbound frames by the time they reach these tracks.

  /** Every decrypted audio track to mix into the recording: all remote mics +
   * the local mic. Returns raw MediaStreamTracks for Web Audio. */
  recordingAudioTracks(): MediaStreamTrack[] {
    if (!this.room) return [];
    const out: MediaStreamTrack[] = [];
    const localMic = this.room.localParticipant.getTrackPublication(Track.Source.Microphone);
    if (localMic?.track?.mediaStreamTrack) out.push(localMic.track.mediaStreamTrack);
    for (const p of this.room.remoteParticipants.values()) {
      const pub = p.getTrackPublication(Track.Source.Microphone);
      const mst = pub?.audioTrack?.mediaStreamTrack;
      if (mst && pub?.isSubscribed && !pub.isMuted) out.push(mst);
    }
    return out;
  }

  /** Visible video sources for the composite frame, in a stable order (screen
   * shares first, then cameras). The UI turns these into a grid/spotlight. */
  recordingVideoSources(): { identity: string; name: string; source: 'camera' | 'screen'; track: MediaStreamTrack }[] {
    if (!this.room) return [];
    const out: { identity: string; name: string; source: 'camera' | 'screen'; track: MediaStreamTrack }[] = [];
    const collect = (p: Participant) => {
      const name = p.name || p.identity;
      const screen = p.getTrackPublication(Track.Source.ScreenShare)?.videoTrack?.mediaStreamTrack;
      if (screen) out.push({ identity: p.identity, name, source: 'screen', track: screen });
    };
    const collectCam = (p: Participant) => {
      const name = p.name || p.identity;
      const cam = p.getTrackPublication(Track.Source.Camera)?.videoTrack?.mediaStreamTrack;
      if (cam) out.push({ identity: p.identity, name, source: 'camera', track: cam });
    };
    collect(this.room.localParticipant);
    for (const p of this.room.remoteParticipants.values()) collect(p);
    collectCam(this.room.localParticipant);
    for (const p of this.room.remoteParticipants.values()) collectCam(p);
    return out;
  }

  /**
   * Audio level 0..1 for meters. Poll from a rAF loop and write to the DOM via
   * refs — never through React state (§9.3.3).
   */
  readAudioLevel(identity: string): number {
    return this.participant(identity)?.audioLevel ?? 0;
  }

  /** Per-attendee transport stats (F-CALL-6). Pull-based: poll only while the
   * stats panel is open. */
  readStats(identity: string): AttendeeStats | null {
    const p = this.participant(identity);
    if (!p) return null;
    const cam = p.getTrackPublication(Track.Source.Camera);
    const mic = p.getTrackPublication(Track.Source.Microphone);
    const dims = cam?.dimensions;
    return {
      identity,
      videoBitrate: cam?.track?.currentBitrate ?? 0,
      audioBitrate: mic?.track?.currentBitrate ?? 0,
      width: dims?.width ?? 0,
      height: dims?.height ?? 0,
    };
  }

  // ── Internals ──────────────────────────────────────────────────────────────

  private participant(identity: string): Participant | undefined {
    if (!this.room) return undefined;
    if (this.room.localParticipant.identity === identity) return this.room.localParticipant;
    return this.room.remoteParticipants.get(identity);
  }

  private snapshotOf(p: Participant): AttendeeSnapshot {
    const has = (source: Track.Source) => {
      const pub = p.getTrackPublication(source);
      return !!pub && !pub.isMuted && (p.isLocal || pub.isSubscribed || !!pub.track);
    };
    return {
      identity: p.identity,
      name: p.name || p.identity,
      avatarUrl: p.attributes?.avatar || undefined,
      isLocal: p.isLocal,
      isSpeaking: this.speaking.has(p.identity),
      quality: toQuality(p.connectionQuality),
      micEnabled: has(Track.Source.Microphone),
      cameraEnabled: has(Track.Source.Camera),
      screenShareEnabled: has(Track.Source.ScreenShare),
      // null (unknown) until this attendee actually publishes something —
      // the ⚠ tile marker mirrors the badge's publishing-only rule.
      encrypted:
        p.getTrackPublications().length === 0 ? null : (this.encrypted.get(p.identity) ?? null),
      handRaisedAt: this.hands.get(p.identity) ?? null,
      joinedAt: p.joinedAt?.getTime() ?? Date.now(),
    };
  }

  private setE2EEState(state: E2EEState): void {
    if (this.e2eeState === state) return;
    this.e2eeState = state;
    this.events.emit('e2eeStateChanged', state);
  }

  /** Badge degradation (F-SEC-1): the room is degraded only when an attendee
   * who is actually PUBLISHING media is unencrypted — the SDK reports
   * everyone as unencrypted until their first encrypted frame, so idle
   * attendees must not flip the badge. */
  private recomputeBadge(): void {
    // Never override a wrong-key verdict (Model B) from track-status churn.
    if (!this.room || (this.e2eeState !== 'on' && this.e2eeState !== 'degraded')) return;
    const everyone: Participant[] = [this.room.localParticipant, ...this.room.remoteParticipants.values()];
    const anyPlaintext = everyone.some(
      (p) => this.encrypted.get(p.identity) === false && p.getTrackPublications().length > 0,
    );
    this.setE2EEState(anyPlaintext ? 'degraded' : 'on');
  }

  private applyHand(identity: string, raisedAt: number | null): void {
    if (raisedAt === null) this.hands.delete(identity);
    else this.hands.set(identity, raisedAt);
    this.events.emit('handChanged', identity, raisedAt);
    const p = this.participant(identity);
    if (p) this.upsert(p);
  }

  private setState(state: RoomConnectionState, reason?: string): void {
    this.state = state;
    this.events.emit('roomStateChanged', state, reason);
  }

  private upsert(p: Participant): void {
    this.events.emit('attendeeUpserted', this.snapshotOf(p));
    this.recomputeBadge();
  }

  private touchLocal(): void {
    const lp = this.room?.localParticipant;
    if (lp) {
      this.upsert(lp);
      this.events.emit('tracksChanged', lp.identity);
    }
  }

  private emitAllAttendees(): void {
    for (const snap of this.snapshots()) this.events.emit('attendeeUpserted', snap);
  }

  private wireRoomEvents(room: Room): void {
    room
      .on(RoomEvent.ParticipantConnected, (p: RemoteParticipant) => {
        this.upsert(p);
        // Re-assert the recording indicator to late joiners (§10.5): they must
        // learn a recording is in progress the moment they connect.
        if (this.recSending && this.recActiveBy) void this.publishRec(true, this.recActiveBy);
      })
      .on(RoomEvent.ParticipantDisconnected, (p: RemoteParticipant) => {
        this.speaking.delete(p.identity);
        this.encrypted.delete(p.identity);
        this.hands.delete(p.identity);
        this.events.emit('attendeeRemoved', p.identity);
      })
      .on(RoomEvent.TrackSubscribed, (_t, _pub, p) => {
        this.upsert(p);
        this.events.emit('tracksChanged', p.identity);
      })
      .on(RoomEvent.TrackUnsubscribed, (_t, _pub, p) => {
        this.upsert(p);
        this.events.emit('tracksChanged', p.identity);
      })
      // Publication add/remove can reach us without a subscription change
      // (e.g. a remote screen share being unpublished) — same upsert path.
      .on(RoomEvent.TrackPublished, (_pub, p: RemoteParticipant) => {
        this.upsert(p);
        this.events.emit('tracksChanged', p.identity);
        // Late-published video honors an active low-bandwidth session (F-CALL-7).
        if (this.lowBandwidth) this.applyLowBandwidth(p);
      })
      .on(RoomEvent.TrackUnpublished, (_pub, p) => {
        this.upsert(p);
        this.events.emit('tracksChanged', p.identity);
      })
      .on(RoomEvent.TrackMuted, (pub, p) => {
        // F-HOST-2: a local mic mute we didn't initiate came from the host.
        if (p.isLocal && pub.source === Track.Source.Microphone && !this.selfMutingMic) {
          this.events.emit('error', 'You were muted by the host');
        }
        this.upsert(p);
      })
      .on(RoomEvent.TrackUnmuted, (_pub, p) => this.upsert(p))
      .on(RoomEvent.LocalTrackPublished, (pub) => {
        // Re-apply blur when the camera (re)starts (F-CALL-5 persists the pref).
        if (this.blurPreferred && pub.source === Track.Source.Camera) {
          const track = this.localCameraTrack();
          if (track && !this.blur.active) void this.blur.enable(track);
        }
        this.touchLocal();
      })
      .on(RoomEvent.LocalTrackUnpublished, () => this.touchLocal())
      .on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
        // Diffed per identity so exactly the affected tiles re-render (§9.3.7a).
        const next = new Set(speakers.map((s) => s.identity));
        for (const id of next) {
          if (!this.speaking.has(id)) this.events.emit('speakingChanged', id, true);
        }
        for (const id of this.speaking) {
          if (!next.has(id)) this.events.emit('speakingChanged', id, false);
        }
        this.speaking = next;
        this.events.emit(
          'activeSpeakersChanged',
          speakers.map((s) => s.identity),
        );
      })
      .on(RoomEvent.ConnectionQualityChanged, (q: ConnectionQuality, p: Participant) => {
        this.events.emit('qualityChanged', p.identity, toQuality(q));
      })
      .on(RoomEvent.Reconnecting, () => this.setState('reconnecting'))
      .on(RoomEvent.Reconnected, () => {
        this.setState('connected');
        this.emitAllAttendees();
      })
      .on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
        // Distinguish a host action from a normal leave (F-HOST-3/5).
        if (reason === DisconnectReason.PARTICIPANT_REMOVED) {
          this.events.emit('removed', 'You were removed from the meeting by the host.');
        } else if (reason === DisconnectReason.ROOM_DELETED) {
          this.events.emit('removed', 'The host ended the meeting.');
        }
        this.setState('disconnected', reason !== undefined ? String(reason) : undefined);
      })
      .on(RoomEvent.ParticipantEncryptionStatusChanged, (encrypted: boolean, p?: Participant) => {
        const identity = p?.identity ?? room.localParticipant.identity;
        this.encrypted.set(identity, encrypted);
        this.events.emit('encryptionChanged', identity, encrypted);
        const participant = this.participant(identity);
        if (participant) this.upsert(participant);
        this.recomputeBadge();
      })
      .on(RoomEvent.EncryptionError, () => {
        // The badge is the user signal — no per-error toast (a room with many
        // mismatched senders would bury the user in identical toasts).
        // In Model B, sustained decryption failure means OUR passphrase is
        // wrong (F-SEC-1 wrong-key guidance); in Model A it's transient
        // degradation.
        this.decryptFailures += 1;
        if (this.e2eeModel === 'b' && this.decryptFailures >= 3) {
          this.setE2EEState('wrong-key');
        } else if (this.e2eeState !== 'wrong-key') {
          this.setE2EEState('degraded');
        }
      })
      .on(
        RoomEvent.DataReceived,
        (raw: Uint8Array, p?: RemoteParticipant, _kind?: DataPacket_Kind, topic?: string) => {
          if (!p) return;
          // Opens run on a serial chain so the reliable channel's ordering
          // survives the async decrypt. openSealed returns null for anything
          // unauthentic — in a sealed room, plaintext injected at the SFU
          // never reaches the parsers below (no downgrade path).
          this.rxChain = this.rxChain
            .then(async () => {
              const key = this.keyProvider?.dataKey;
              const payload = key ? await openSealed(key, topic ?? '', raw) : raw;
              if (!payload) return;
              switch (topic) {
                case CHAT_TOPIC: {
                  const wire = decodeChat(payload);
                  if (wire) {
                    this.events.emit('chatMessage', {
                      id: wire.id,
                      fromIdentity: p.identity,
                      fromName: p.name || p.identity,
                      text: wire.text,
                      ts: wire.ts,
                    });
                  }
                  break;
                }
                case REACTION_TOPIC: {
                  const emoji = decodeReaction(payload);
                  if (emoji) this.events.emit('reaction', p.identity, p.name || p.identity, emoji);
                  break;
                }
                case POLL_TOPIC: {
                  const wire = decodePoll(payload);
                  if (wire) this.events.emit('poll', p.identity, wire);
                  break;
                }
                case TIMER_TOPIC: {
                  const wire = decodeTimer(payload);
                  if (wire) this.events.emit('timer', p.identity, wire);
                  break;
                }
                case HAND_TOPIC: {
                  const wire = decodeHand(payload);
                  if (wire) {
                    const subject = wire.target ?? p.identity;
                    this.applyHand(subject, wire.raised ? wire.ts : null);
                  }
                  break;
                }
                case PCHAT_TOPIC: {
                  const wire = decodeChat(payload);
                  if (wire) {
                    this.events.emit('privateChat', p.identity, {
                      id: wire.id,
                      fromIdentity: p.identity,
                      fromName: p.name || p.identity,
                      text: wire.text,
                      ts: wire.ts,
                    });
                  }
                  break;
                }
                case FILE_TOPIC: {
                  const ref = decodeFileRef(payload);
                  if (ref) {
                    this.events.emit('fileShared', p.identity, p.name || p.identity, {
                      fileId: ref.fileId,
                      objectKey: ref.objectKey,
                      name: ref.name,
                      size: ref.size,
                      mime: ref.mime,
                      nonceB64: ref.nonceB64,
                      ts: Date.now(),
                    });
                  }
                  break;
                }
                case REC_TOPIC: {
                  const w = decodeRec(payload);
                  if (w) this.onRecSignal(w.on, w.by || p.name || p.identity);
                  break;
                }
              }
            })
            .catch(() => {});
        },
      )
      .on(RoomEvent.MediaDevicesChanged, () => {
        void this.handleDevicesChanged();
      })
      .on(RoomEvent.MediaDevicesError, (e: Error) => {
        // Missing-device failures are handled (with a friendlier message and
        // fallback) in setCaptureEnabled — don't double-toast the raw error.
        if (isMissingDeviceError(e)) return;
        this.events.emit('error', `media device error: ${e.message}`);
      });
  }

  /** Unplug handling (F-CALL-3): if an active device vanished, the SDK falls
   * back to a default; surface it so the UI can toast. */
  private async handleDevicesChanged(): Promise<void> {
    this.events.emit('mediaDevicesChanged');
    if (!this.room) return;
    for (const kind of ['audioinput', 'videoinput'] as MediaDeviceKind[]) {
      const active = this.room.getActiveDevice(kind);
      if (!active) continue;
      const available = await this.devices.list(kind);
      if (!available.some((d) => d.deviceId === active)) {
        const fallback = available[0];
        if (fallback) {
          await this.room.switchActiveDevice(kind, fallback.deviceId).catch(() => {});
          this.events.emit('deviceFallback', kind, fallback.label || 'default device');
        }
      }
    }
  }
}
